<script context="module">
  /**
   * calculate start, previousPages, visiblePages, nextPages, end of a paginated list.
   * export for testing in isolation
   *
   * @param pages
   * @param maxNavigatablePages
   * @param currentPage
   */
  export function createSelectablePages(pages = 0, maxNavigatablePages = 5, currentPage = 1) {
    const shouldEllipse = pages > maxNavigatablePages;
    const shouldEllipsePrevious = shouldEllipse && currentPage > maxNavigatablePages;
    const shouldEllipseNext = shouldEllipse && pages - currentPage > maxNavigatablePages - 1;
    const hasPagesOtherThanStartAndEnd = pages > 2;

    let previousPages = [];
    let visiblePages = [];
    let nextPages = [];

    if (hasPagesOtherThanStartAndEnd && !shouldEllipse) {
      // show all remaining pages in addition to start and end
      visiblePages = Array(pages - 2)
        .fill()
        .map((_, i) => i + 2);
    } else if (hasPagesOtherThanStartAndEnd && !shouldEllipsePrevious) {
      // show all pages up to max
      visiblePages = Array(maxNavigatablePages + 1 - 2)
        .fill()
        .map((_, i) => i + 2);
      // ellipse all pages from max to end, exclusive
      nextPages = nextPages = Array(pages - 1 - maxNavigatablePages)
        .fill()
        .map((_, i) => i + maxNavigatablePages + 1);
    } else if (shouldEllipsePrevious && shouldEllipseNext) {
      // show 3 adjacent pages
      visiblePages = [currentPage - 1, currentPage, currentPage + 1];
      // ellipse all pages from start to currentPage - 1, exclusive
      previousPages = Array(currentPage - 3)
        .fill()
        .map((_, i) => i + 2);
      // ellipse all pages from currentPage + 1 to end, exclusive
      nextPages = Array(pages - 3 - maxNavigatablePages)
        .fill()
        .map((_, i) => i + maxNavigatablePages + 3);
    } else if (shouldEllipsePrevious && !shouldEllipseNext) {
      // show the last max pages up to max, exclusive
      visiblePages = Array(maxNavigatablePages - 1)
        .fill()
        .map((_, i) => i + (pages - maxNavigatablePages + 1));
      // ellipse all previous pages up to last max
      previousPages = Array(pages - maxNavigatablePages - 1)
        .fill()
        .map((_, i) => i + 2);
    }

    return {
      start: pages >= 1 ? 1 : null,
      previousPages,
      visiblePages,
      nextPages,
      end: pages >= 1 ? pages : null
    };
  }

  /**
   * for use in parent component to paginate given list of items based on page size
   *
   * @param items
   * @param itemsPerPage
   */
  export function paginateItems(items = [], itemsPerPage = 1) {
    const numOfPages = Math.ceil(items.length / itemsPerPage);
    const paginatedItems = Array(numOfPages)
      .fill()
      .map((_, i) => {
        return items.slice(i * itemsPerPage, (i + 1) * itemsPerPage);
      });

    return {
      numOfPages,
      paginatedItems
    };
  }

  export function getPageStats({
    itemsPerPage = 10,
    numOfItems = 0,
    numOfPages = 0,
    currentPage = 1
  }) {
    const from = numOfPages > 1 ? (currentPage - 1) * itemsPerPage + 1 : numOfItems > 0 ? 1 : 0;
    const total = numOfItems || numOfPages * itemsPerPage;
    const to =
      numOfPages > 1
        ? currentPage === numOfPages
          ? total
          : currentPage * itemsPerPage
        : numOfItems;

    return {
      from,
      to,
      total
    };
  }
</script>

<script>
  import { createEventDispatcher } from "svelte";
  import Button from "$lib/components/Button.svelte";
  import Icon from "../Icon.svelte";
  import PageSelector from "./PageSelector.svelte";
  import PageSelectorDropdown from "./PageSelectorDropdown.svelte";
  import Heading from "$lib/components/Heading.svelte";

  export let numOfItems = 0;
  export let numOfPages = 0;
  export let maxNavigatablePages = 5;
  export let initialActivePage = 1;
  export let itemsPerPage = 10;
  export let id = "default-pagination";
  export let alignLabelCenter = false;

  let clazz = "";
  export { clazz as class };

  $: currentPage = initialActivePage;
  $: ({ start, previousPages, visiblePages, nextPages, end } = createSelectablePages(
    numOfPages,
    maxNavigatablePages,
    currentPage
  ));

  $: ({ from, to, total } = getPageStats({ itemsPerPage, numOfItems, numOfPages, currentPage }));

  function ariaLabelContext(defaultLabel, pageIdentifier) {
    if (String(currentPage) == pageIdentifier) {
      return `${defaultLabel}, current page`;
    } else {
      return `${defaultLabel}`;
    }
  }

  const dispatch = createEventDispatcher();
  function navigateTo(pageNum) {
    let page;
    if (pageNum > 0 && pageNum <= numOfPages) {
      page = pageNum;
    } else if (pageNum <= 0) {
      // to last page
      page = numOfPages;
    } else if (pageNum > numOfPages) {
      // to first page
      page = 1;
    }

    currentPage = page;
    dispatch("navigate", {
      page
    });
  }
</script>

{#if numOfPages > 1}
  <nav
    aria-labelledby={id}
    class="w-full flex flex-col px-[7%] lg:px-[5%] {clazz}"
    data-testid={id}
  >
    <Heading idOverride={id} class="sr-only" text="Pagination" />
    <ul
      aria-labelledby={id}
      class="list-none grid place-items-center grid-cols-2 gap-40"
      class:md:grid-cols-12={!alignLabelCenter}
    >
      <li
        class="block list-none col-span-1 order-2 justify-self-start"
        class:md:col-span-2={!alignLabelCenter}
      >
        <Button
          variant="outline"
          size="small"
          class="!pr-16 !pl-8 text-size-14"
          on:click={() => navigateTo(currentPage - 1)}
          testid="btn-prev"
          aria-label="Previous page"
        >
          <span class="inline-flex justify-center place-items-center gap-2">
            <Icon type="chevron-left" leading />
            Previous
          </span>
        </Button>
      </li>

      <li
        class="col-span-full order-1"
        class:md:col-span-8={!alignLabelCenter}
        class:md:order-2={!alignLabelCenter}
        data-testid="navigatable-pages-container"
      >
        <ul
          aria-labelledby={id}
          class="list-none flex justify-center items-center sm:gap-16 md:gap-24 gap-12"
        >
          <li>
            <PageSelector
              pageNum={start}
              current={start === currentPage}
              on:click={() => navigateTo(start)}
              arialLabel={ariaLabelContext("First page", start)}
              testid="btn-navigatable-page-{start}"
            />
          </li>

          {#if previousPages.length > 0}
            <li>
              <PageSelectorDropdown
                pageNums={previousPages}
                onSelect={navigateTo}
                arialLabel="Previous pages dropdown"
                testid="btn-previous-pages"
              />
            </li>
          {/if}

          {#each visiblePages as pageNum}
            <li>
              <PageSelector
                {pageNum}
                current={pageNum === currentPage}
                on:click={() => navigateTo(pageNum)}
                arialLabel={ariaLabelContext(`Page ${pageNum}`, pageNum)}
                testid="btn-navigatable-page-{pageNum}"
              />
            </li>
          {/each}

          {#if nextPages.length > 0}
            <li>
              <PageSelectorDropdown
                pageNums={nextPages}
                onSelect={navigateTo}
                arialLabel="Next pages dropdown"
                testid="btn-next-pages"
              />
            </li>
          {/if}

          {#if end > start}
            <li>
              <PageSelector
                pageNum={end}
                current={end === currentPage}
                on:click={() => navigateTo(end)}
                arialLabel={ariaLabelContext("Last page", end)}
                testid="btn-navigatable-page-{end}"
              />
            </li>
          {/if}
        </ul>
      </li>
      <li
        class="col-span-1 md:order-3 order-3 justify-self-end"
        class:md:col-span-2={!alignLabelCenter}
      >
        <Button
          variant="outline"
          size="small"
          class="text-size-14"
          on:click={() => navigateTo(currentPage + 1)}
          testid="btn-next"
          aria-label="Next page"
          icon={{ trailing: true, type: "chevron-right" }}
        >
          <span class="inline-flex justify-center place-items-center gap-2"> Next </span>
        </Button>
      </li>
    </ul>

    <hr aria-hidden="true" class="md:my-24 my-40 border-neutral-300" />

    <div
      class="flex md:flex-row flex-col justify-center items-center gap-y-16 text-size-12"
      class:md:justify-between={!alignLabelCenter}
    >
      <div aria-live="polite" aria-atomic="true">
        Viewing {from} to {to} of {total} items
      </div>
    </div>
  </nav>
{/if}
